<script lang="ts">
	import type { Snippet } from "svelte";
	import { Section, SectionHeader } from "#lib/web-ui/primitives/index.js";
	import Skeleton from "#lib/web-ui/ui/skeleton/skeleton.svelte";
	import ExternalLinkIcon from "@lucide/svelte/icons/external-link";
	import PlayIcon from "@lucide/svelte/icons/play";
	import TvMinimalPlayIcon from "@lucide/svelte/icons/tv-minimal-play";
	import type { YouTubeSectionProps } from "./youtube.types";

	let {
		label,
		title,
		subtitle,
		videos,
		channelUrl,
		theme = "muted",
		class: className,
		footer
	}: YouTubeSectionProps & { footer?: Snippet } = $props();

	function formatDate(dateStr: string): string {
		return new Date(dateStr).toLocaleDateString("en-US", {
			month: "short",
			day: "numeric",
			year: "numeric"
		});
	}
</script>

<Section {theme} class={className}>
	<SectionHeader {label} {title} {subtitle} />

	<div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
		{#if videos.length === 0}
			{#each Array(3) as _}
				<div
					class="bg-card border-border flex flex-col overflow-hidden rounded border shadow-sm"
				>
					<Skeleton class="aspect-video w-full rounded-none" />
					<div class="flex flex-col gap-2 p-4">
						<div class="flex items-center gap-2">
							<Skeleton class="h-3 w-3 rounded-full" />
							<Skeleton class="h-3 w-24" />
							<Skeleton class="h-3 w-16" />
						</div>
						<Skeleton class="h-4 w-full" />
						<Skeleton class="h-4 w-3/4" />
					</div>
				</div>
			{/each}
		{/if}
		{#each videos as video (video.videoId)}
			<a
				href="https://www.youtube.com/watch?v={video.videoId}"
				target="_blank"
				rel="noopener noreferrer"
				class="bg-card group border-border flex flex-col overflow-hidden rounded border shadow-sm transition-shadow hover:shadow-md"
			>
				<div class="relative aspect-video overflow-hidden">
					<img
						src={video.thumbnailUrl}
						alt={video.title}
						class="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
						width="480"
						height="270"
						loading="lazy"
					/>
					<div
						class="absolute inset-0 flex items-center justify-center bg-black/0 transition-colors group-hover:bg-black/30"
					>
						<span
							class="bg-primary text-primary-foreground scale-90 rounded-full p-3 opacity-0 shadow-lg transition-all group-hover:scale-100 group-hover:opacity-100"
						>
							<PlayIcon class="ml-0.5 h-5 w-5" />
						</span>
					</div>
				</div>

				<div class="flex grow flex-col p-4">
					<div class="mb-2 flex items-center gap-2 text-xs">
						<TvMinimalPlayIcon class="text-muted-foreground h-3 w-3" />
						<span class="text-muted-foreground font-medium">{video.channelTitle}</span>
						<span class="text-muted-foreground">·</span>
						<span class="text-muted-foreground">{formatDate(video.publishedAt)}</span>
					</div>

					<h3
						class="text-card-foreground line-clamp-2 text-sm leading-snug font-semibold"
					>
						{video.title}
					</h3>
				</div>
			</a>
		{/each}
	</div>

	{#if footer}
		{@render footer()}
	{:else if channelUrl}
		<div class="mt-10 text-center">
			<a
				href={channelUrl}
				target="_blank"
				rel="noopener noreferrer"
				class="text-primary hover:text-primary/80 border-primary/30 hover:border-primary/60 inline-flex items-center gap-1.5 rounded-md border px-4 py-2 text-sm font-medium transition-colors"
			>
				View all videos
				<ExternalLinkIcon class="h-3.5 w-3.5" />
			</a>
		</div>
	{/if}
</Section>
